import { PoseLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import type { NormalizedLandmark } from "@mediapipe/tasks-vision";

// Cache the WASM fileset (shared across all instances)
let visionPromise: ReturnType<typeof FilesetResolver.forVisionTasks> | null = null;

function getVision() {
  if (!visionPromise) {
    visionPromise = FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm",
    );
  }
  return visionPromise;
}

/**
 * Create a new PoseLandmarker instance. Each video player should have its own
 * instance so their internal tracker states don't interfere.
 */
export async function createPoseLandmarker(): Promise<PoseLandmarker> {
  const vision = await getVision();

  const options = {
    baseOptions: {
      modelAssetPath:
        "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
      delegate: "GPU" as const,
    },
    runningMode: "VIDEO" as const,
    numPoses: 1,
    minPoseDetectionConfidence: 0.3,
    minTrackingConfidence: 0.3,
  };

  try {
    return await PoseLandmarker.createFromOptions(vision, options);
  } catch {
    console.warn("GPU delegate failed, falling back to CPU");
    options.baseOptions.delegate = "CPU" as any;
    return await PoseLandmarker.createFromOptions(vision, options);
  }
}

export interface PoseResult {
  landmarks: NormalizedLandmark[];
  timestamp: number;
}

export function detectPose(
  landmarker: PoseLandmarker,
  video: HTMLVideoElement,
  timestampMs: number,
): PoseResult | null {
  const result = landmarker.detectForVideo(video, timestampMs);

  if (result.landmarks && result.landmarks.length > 0) {
    return {
      landmarks: result.landmarks[0],
      timestamp: timestampMs,
    };
  }

  return null;
}
